/**
 * Utility functions for generating meta tags
 */

interface MetaTagsConfig {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  locale?: string;
  twitterHandle?: string;
  author?: string;
  keywords?: string[];
  robots?: string;
  viewport?: string;
}

/**
 * Generate Open Graph meta tags
 */
export const generateOpenGraphTags = (config: MetaTagsConfig) => {
  const tags: { [key: string]: string } = {
    'og:title': config.title,
    'og:description': config.description,
    'og:type': config.type || 'website',
    'og:site_name': config.siteName || 'Shef',
    'og:locale': config.locale || 'en_US',
  };

  if (config.url) {
    tags['og:url'] = config.url;
  }

  if (config.image) {
    tags['og:image'] = config.image;
    tags['og:image:alt'] = config.title;
  }

  return tags;
};

/**
 * Generate Twitter Card meta tags
 */
export const generateTwitterCardTags = (config: MetaTagsConfig) => {
  const tags: { [key: string]: string } = {
    'twitter:card': config.image ? 'summary_large_image' : 'summary',
    'twitter:title': config.title,
    'twitter:description': config.description,
  };

  if (config.twitterHandle) {
    tags['twitter:site'] = config.twitterHandle;
    tags['twitter:creator'] = config.twitterHandle;
  }

  if (config.image) {
    tags['twitter:image'] = config.image;
    tags['twitter:image:alt'] = config.title;
  }

  return tags;
};

/**
 * Generate standard meta tags
 */
export const generateStandardMetaTags = (config: MetaTagsConfig) => {
  const tags: { [key: string]: string } = {
    description: config.description,
    viewport: config.viewport || 'width=device-width, initial-scale=1',
    robots: config.robots || 'index, follow',
  };

  if (config.author) {
    tags.author = config.author;
  }

  if (config.keywords && config.keywords.length > 0) {
    tags.keywords = config.keywords.join(', ');
  }

  return tags;
};

/**
 * Combine all meta tags
 */
export const generateAllMetaTags = (config: MetaTagsConfig) => ({
  standard: generateStandardMetaTags(config),
  openGraph: generateOpenGraphTags(config),
  twitter: generateTwitterCardTags(config),
});

/**
 * Truncate description to optimal length
 */
export const truncateDescription = (description: string, maxLength = 155): string => {
  if (description.length <= maxLength) {
    return description;
  }
  return `${description.substring(0, maxLength - 3).trim()}...`;
};

/**
 * Generate page-specific title with brand
 */
export const generatePageTitle = (pageTitle: string, brandName = 'Shef'): string => {
  if (!pageTitle) {
    return brandName;
  }
  // Keep titles under 60 characters for optimal display
  const separator = ' | ';
  const maxLength = 60;
  const fullTitle = `${pageTitle}${separator}${brandName}`;

  if (fullTitle.length <= maxLength) {
    return fullTitle;
  }

  // Truncate page title if needed
  const availableLength = maxLength - brandName.length - separator.length - 3;
  if (availableLength <= 0) {
    return brandName; // Fallback to brand if nothing fits
  }
  return `${pageTitle.substring(0, availableLength).trim()}...${separator}${brandName}`;
};
